import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import WheyIcon from '@assets/icons/whey.svg';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { type FC, useEffect } from 'react';
import { OriginalInboundContent } from '@/contexts/original-inbound-content';
import { OriginalInboundSkeleton } from '@/screens/order-inbounds/original-inbound-skeleton';
import { SCREEN_PADDING_SMALL } from '@/styles';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOriginalInboundMetadataQuery } from '@/hooks/use-original-inbound-metadata-query';
import { useWindowDimensions } from 'react-native';
import type { GetOrderSmartOrderMetadata } from '@/screens/order-inbounds/types';

interface Props {
  filterBySubmittedOrderUUID?: string;
  onClose: () => void;
  orderMetadata: GetOrderSmartOrderMetadata;
}

export const OrderDetailOriginalInboundModal: FC<Props> = ({
  filterBySubmittedOrderUUID,
  onClose,
  orderMetadata,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const { height } = useWindowDimensions();

  const [styles] = useStyles();

  const { data, loading, error } = useOriginalInboundMetadataQuery({
    filterBySubmittedOrderUUID,
    orderMetadata,
  });

  useEffect(() => {
    if (error) {
      Analytics.track('order_detail_original_inbound_modal.query_error', {
        error,
        filterBySubmittedOrderUUID,
        orderMetadata,
      });
    }
  }, [error, filterBySubmittedOrderUUID, orderMetadata]);

  const modalContentStyle = { flex: 1, maxHeight: height * 0.8 };

  return (
    <BaseActionSheet
      contentContainerStyle={!isSmallLayout ? modalContentStyle : null}
      contentStyle={[
        styles.inboundModalContent,
        styles.inboundModalContentNoPadding,
      ]}
      isVisible
      modalHeaderStyle={styles.inboundsBackground}
      modalStyle={[
        styles.inboundsBackground,
        !isSmallLayout ? modalContentStyle : null,
      ]}
      onClose={onClose}
      title={i18n.t('original_inbound')}
    >
      {loading ? (
        <OriginalInboundSkeleton />
      ) : error ? (
        <EmptyStateMinimal
          ImageComponent={WheyIcon}
          text={i18n.t('toast.standard_error')}
        />
      ) : (
        <OriginalInboundContent isSummaryView orderMetadata={data} />
      )}
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  inboundModalContent: {
    backgroundColor: 'transparent',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    paddingBottom: SCREEN_PADDING_SMALL,
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  inboundModalContentNoPadding: {
    paddingHorizontal: 0,
    paddingVertical: 0,
  },
  inboundsBackground: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
}));
